import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { PageHeader, StatTile } from '@/lib/atsUi'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, placements, invoices } from '@/lib/mocks'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataList } from '@/components/ui/data-list'
import { Tabs } from '@/components/ui/tabs'
import { Download } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/placements')({
  component: PlacementsPage,
})

type Tab = 'placements' | 'invoices'

function PlacementsPage() {
  const [tab, setTab] = useState<Tab>('placements')
  const guaranteeActiveCount = placements.filter((p) => p.status === 'guarantee_active').length

  return (
    <DashboardLayout title="Placements & billing" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Placements & billing"
        subtitle="Closed deals, the guarantee clock, and your invoices — in one place."
        action={
          <Button variant="outline" size="sm" onClick={() => toast.success('Export started')}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-8">
        <StatTile label="Placements (90 days)" value={placements.length} />
        <StatTile label="Revenue this month" value="₹8.3 L" />
        <StatTile label="In guarantee window" value={guaranteeActiveCount} />
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <Tabs
          items={[
            { value: 'placements', label: 'Placements', count: placements.length },
            { value: 'invoices', label: 'Invoices', count: invoices.length },
          ]}
          value={tab}
          onChange={(v) => setTab(v as Tab)}
        />
      </div>

      {/* Placements tab */}
      {tab === 'placements' && (
        <DataList
          items={placements}
          getKey={(p) => p.id}
          pageSize={5}
          empty="No placements yet."
          renderRow={(placement) => (
            <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:gap-4 hover:bg-muted/40 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm leading-snug">{placement.candidateName}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {placement.jobTitle} · {placement.client}
                </p>
              </div>
              <div className="text-xs text-muted-foreground sm:w-28 shrink-0">
                <span className="block text-foreground text-sm">{placement.joiningDate}</span>
              </div>
              <div className="hidden lg:block text-xs text-muted-foreground sm:w-20 shrink-0">
                <span className="block text-foreground text-sm">{placement.ctc}</span>
                <span>CTC</span>
              </div>
              <div className="text-xs text-muted-foreground sm:w-20 shrink-0">
                <span className="block text-foreground text-sm">{placement.feeAmount}</span>
                <span>Fee</span>
              </div>
              <div className="hidden lg:block text-xs text-muted-foreground sm:w-28 shrink-0">
                <span className="block text-xs">Guarantee ends</span>
                <span className="text-foreground text-sm">{placement.guaranteeEnds}</span>
              </div>
              <div className="shrink-0">
                {placement.status === 'guarantee_active' && <Badge variant="warning">In guarantee</Badge>}
                {placement.status === 'offered' && <Badge variant="info">Offer out</Badge>}
                {placement.status === 'joined' && <Badge variant="success">Joined</Badge>}
              </div>
            </div>
          )}
        />
      )}

      {/* Invoices tab */}
      {tab === 'invoices' && (
        <DataList
          items={invoices}
          getKey={(i) => i.id}
          pageSize={5}
          empty="No invoices yet."
          renderRow={(invoice) => (
            <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:gap-4 hover:bg-muted/40 transition-colors">
              <div className="sm:w-36 shrink-0">
                <p className="text-sm font-mono tracking-tight">{invoice.number}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug">{invoice.client}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{invoice.candidate}</p>
              </div>
              <div className="hidden lg:block shrink-0 sm:w-36">
                <p className="text-sm font-medium">{invoice.total}</p>
                <p className="text-xs text-muted-foreground">incl. 18% GST · SAC {invoice.sac}</p>
              </div>
              <div className="hidden lg:block text-xs text-muted-foreground sm:w-28 shrink-0">
                <span className="block text-xs">Due</span>
                <span className="text-foreground text-sm">{invoice.dueDate}</span>
              </div>
              <div className="shrink-0">
                {invoice.status === 'paid' && <Badge variant="success">Paid</Badge>}
                {invoice.status === 'sent' && <Badge variant="info">Sent</Badge>}
                {invoice.status === 'draft' && <Badge variant="info">Draft</Badge>}
              </div>
            </div>
          )}
        />
      )}
    </DashboardLayout>
  )
}
